const { SENDING_TYPES, SENDING_HASHABLE_FIELDS } = require('../constants/_sendings')
const { WEBSOCKET_CHANNELS } = require('../constants/misc')

const { isSimpleObject, isPhoneNumber } = require('../validators')

const { SETTINGS_SITE_SCHEMA } = require('./site')
const { $defaulter } = require('./utils')
const { REGEX_IPV4_WITH_MASK_OR_PATTERN } = require('../constants/regexp')
const { BILLING_CURRENCY } = require('../constants/_billings')

const { NOTIFICATIONS_SETTINGS_MY, NOTIFICATIONS_SETTINGS_CHILD } = require('./notifications')
const { REGEXP_IS_EMAIL_SIMPLE } = require('../constants/regexp')

const defaultHandler_integerOrDefaultValue = (value, schema) => {
  if (typeof value === 'string') {
    value = parseInt(value)
    if (isNaN(value)) {
      value = schema.$default
    }
  } else if (typeof value !== 'number') {
    value = schema.$default
  }

  return value
}

const SETTINGS_USER_SCHEMA = {
  'sendings.allowDuplicates': {
    $default: false,
    $options: [ { $value: false }, { $value: true } ]
  },
  'sendings.duplicates.hashFields': {
    $default: SENDING_HASHABLE_FIELDS,
    $multiple: true,
    $options: SENDING_HASHABLE_FIELDS.map($value => ({ $value }))
  },
  'sendings.duplicates.timeout': {
    $default: 30 * 60,
    $handler: defaultHandler_integerOrDefaultValue,
    $cleaner: value => {
      value = parseInt(value)
      if (!isNaN(value)) {
        return value
      }
    },
    $defaulter
  },
  'sendings.rateLimit.maxAmount': {
    $default: 0,
    $handler: defaultHandler_integerOrDefaultValue,
    $cleaner: value => {
      value = parseInt(value)
      if (!isNaN(value)) {
        return value
      }
    },
    $defaulter
  },
  'sendings.rateLimit.timeout': {
    $default: 0,
    $handler: defaultHandler_integerOrDefaultValue,
    $cleaner: value => {
      value = parseInt(value)
      if (!isNaN(value)) {
        return value
      }
    },
    $defaulter
  },
  'sendings.sendernamesCheck': {
    $default: SENDING_TYPES,
    $multiple: true,
    $options: SENDING_TYPES.map($value => ({ $value }))
  },
  'sendings.patternsCheck': {
    $default: [],
    $multiple: true,
    $options: SENDING_TYPES.map($value => ({ $value }))
  },
  'sendings.tilda.sendFromForms': {
    $default: false,
    $options: [ { $value: false }, { $value: true } ]
  },
  'sendings.phoneNumber.checkMobile': {
    $default: true,
    $options: [ { $value: false }, { $value: true } ]
  },

  'billings.overdraft': {
    $default: 0,
    $handler: v => {
      v = parseFloat(v)
      if (isNaN(v) || v < 0) {
        v = 0
      }

      return v
    },
    $cleaner: value => {
      value = parseFloat(value)
      if (!isNaN(value) && value >= 0) {
        return value
      }
    },
    $defaulter
  },
  'billings.autoAccrual': {
    $default: {
      limit: 0,
      requiredBalance: 0
    },
    $handler: (value, schema) => {
      if (typeof value !== 'object' || value === null) {
        return schema.$default
      }

      const result = {}

      result.limit = parseFloat(value.limit)
      if (isNaN(result.limit)) {
        result.limit = 0
      }

      result.requiredBalance = parseFloat(value.requiredBalance)
      if (isNaN(result.requiredBalance) || result.requiredBalance < 0 || result.requiredBalance < result.limit) {
        result.requiredBalance = 0
      }

      return result
    },
    $cleaner: value => {
      const result = {}

      if (isSimpleObject(value)) {
        const limit = parseFloat(value.limit)
        if (!isNaN(limit)) {
          result.limit = limit
        }
        if (typeof result.limit === 'number') {
          const requiredBalance = parseFloat(value.requiredBalance)
          if (!isNaN(requiredBalance) && requiredBalance >= 0 && requiredBalance >= result.limit) {
            result.requiredBalance = requiredBalance
          }
        }
      }

      return result
    },
    $defaulter
  },
  'billings.processChildrenOperations': {
    $default: true,
    $options: [ { $value: false }, { $value: true } ]
  },
  'billings.billOnErrors': {
    $default: false,
    $options: [ { $value: false }, { $value: true } ]
  },
  'billings.currency': {
    $default: BILLING_CURRENCY.RUB,
    $options: [
      { $value: BILLING_CURRENCY.RUB },
      { $value: BILLING_CURRENCY.EUR },
      { $value: BILLING_CURRENCY.USD },
      { $value: BILLING_CURRENCY.MXN }
    ]
  },

  'referral.billings.processedChildOperations': {
    $default: [
      {
        source: 'accrual',
        percent: 0,
        limitMin: 0,
        limitMax: 0
      }
    ],
    $handler: v => {
      if (!Array.isArray(v)) {
        return []
      }

      return v.reduce((result, operation) => {
        if (
          !operation ||
          typeof operation.source !== 'string' ||
          !operation.source ||
          !(typeof operation.percent === 'number' || typeof operation.percent === 'string')
        ) {
          return result
        }

        if (~result.findIndex(op => op.source === operation.source)) {
          return result
        }

        if (typeof operation.percent === 'string') {
          operation.percent = parseFloat(operation.percent)
        }
        if (typeof operation.percent !== 'number' || isNaN(operation.percent) || operation.percent <= 0) {
          return result
        }

        [ 'limitMin', 'limitMax' ].forEach(key => {
          if (typeof operation[key] === 'string') {
            try {
              operation[key] = parseInt(operation[key])
            } catch (error) {
              operation[key] = 0
            }
          }
          if (typeof operation[key] !== 'number' || isNaN(operation[key]) || operation[key] < 0) {
            operation[key] = 0
          }
        })

        result.push({
          source: operation.source,
          percent: operation.percent,
          limitMin: operation.limitMin,
          limitMax: operation.limitMax
        })

        return result
      }, [])
    },
    $cleaner: value => {
      let result

      if (Array.isArray(value) && value.length) {
        result = []

        const isNumberGtZero = value => typeof value === 'number' || !isNaN(value) || value > 0

        for (const operation of value) {
          if (typeof operation.source !== 'string' || !operation.source || ~result.findIndex(op => op.source === operation.source)) {
            continue
          }

          if (typeof operation.percent === 'string') {
            operation.percent = parseFloat(operation.percent)
            if (!isNumberGtZero(operation.percent)) {
              continue
            }
          }

          for (const key of [ 'limitMin', 'limitMax' ]) {
            operation[key] = parseInt(operation[key])
            if (typeof operation[key] !== 'number' || isNaN(operation[key]) || operation[key] < 0) {
              operation[key] = 0
            }
          }

          result.push({
            source: operation.source,
            percent: operation.percent,
            limitMin: operation.limitMin,
            limitMax: operation.limitMax
          })
        }
      }

      return result
    },
    $defaulter
  },

  'websocket.channels': {
    $default: [],
    $multiple: true,
    $options: WEBSOCKET_CHANNELS.map($value => ({ $value }))
  },

  'smpp.enabled': {
    $default: true,
    $options: [ { $value: false }, { $value: true } ]
  },
  'smpp.rudeMode': {
    $default: false,
    $options: [ { $value: false }, { $value: true } ]
  },
  'smpp.defaultMessageType': {
    $default: SENDING_TYPES.sms,
    $options: SENDING_TYPES.map($value => ({ $value }))
  },
  'smpp.ipWhitelist': {
    $default: [],
    $cleaner: value => {
      if (!Array.isArray(value)) {
        return []
      }

      return value.filter(v => REGEX_IPV4_WITH_MASK_OR_PATTERN.test(v))
    },
    $defaulter
  },

  webhooks: require('./user.webhooks'),

  'notifications.enabled': SETTINGS_SITE_SCHEMA['notifications.enabled'], // FIXME: старое удалить после полного перехода на notifications nestjs
  'notifications.events.billings.limit': SETTINGS_SITE_SCHEMA['notifications.events.billings.limit'], // FIXME: старое удалить после полного перехода на notifications nestjs
  ...NOTIFICATIONS_SETTINGS_MY,
  ...NOTIFICATIONS_SETTINGS_CHILD,
  'notifications.email.recipients': {
    $default: [],
    $cleaner: emails => {
      return Array.isArray(emails) ? emails.filter(email => typeof email === 'string' && REGEXP_IS_EMAIL_SIMPLE.test(email)) : []
    },
    $defaulter
  },
  'notifications.sms.recipients': {
    $default: [],
    $cleaner: phones => {
      if (!Array.isArray(phones)) {
        return undefined
      }

      const clearPhones = []
      for (const phone of phones) {
        const { isValid, formatted } = isPhoneNumber(phone)
        if (isValid) {
          clearPhones.push(formatted)
        }
      }

      return clearPhones
    },
    $defaulter
  },

  'integrations.pyrus.credentials': {
    $default: {
      login: '',
      security_key: ''
    },
    $cleaner: value => {
      const result = {}

      if (typeof value !== 'object' || value === null) {
        return result
      }

      if (typeof value.login === 'string' && value.login) {
        result.login = value.login
      }
      if (typeof value.security_key === 'string' && value.security_key) {
        result.security_key = value.security_key
      }

      return result
    },
    $defaulter
  },

  'reports.maxConcurrentGenerating': {
    $default: 1,
    $cleaner: value => {
      value = parseInt(value)
      if (!isNaN(value) && value >= 0 && value < 10) {
        return value
      }
    },
    $defaulter
  }
}
exports.SETTINGS_USER_SCHEMA = SETTINGS_USER_SCHEMA

// Настройки доступные обычным юзерам
const SETTINGS_USER_SCHEMA_ME = {
  webhooks: SETTINGS_USER_SCHEMA.webhooks,

  'notifications.events.billings.limit': SETTINGS_USER_SCHEMA['notifications.events.billings.limit'], // FIXME: старое
  ...NOTIFICATIONS_SETTINGS_MY,
  'notifications.email.recipients': SETTINGS_USER_SCHEMA['notifications.email.recipients'],
  'notifications.sms.recipients': SETTINGS_USER_SCHEMA['notifications.sms.recipients']
}

exports.SETTINGS_USER_SCHEMA_ME = SETTINGS_USER_SCHEMA_ME

// Настройки, которые реселлер может менять своим дочерним юзерам
const SETTINGS_USER_SCHEMA_RESELLER_CHILD = {
  'sendings.sendernamesCheck': SETTINGS_USER_SCHEMA['sendings.sendernamesCheck'],
  'sendings.patternsCheck': SETTINGS_USER_SCHEMA['sendings.patternsCheck'],
  'sendings.allowDuplicates': SETTINGS_USER_SCHEMA['sendings.allowDuplicates'],
  'sendings.duplicates.hashFields': SETTINGS_USER_SCHEMA['sendings.duplicates.hashFields'],
  'sendings.duplicates.timeout': SETTINGS_USER_SCHEMA['sendings.duplicates.timeout'],

  webhooks: SETTINGS_USER_SCHEMA.webhooks,

  'notifications.events.billings.limit': SETTINGS_USER_SCHEMA['notifications.events.billings.limit'], // FIXME: старое
  ...NOTIFICATIONS_SETTINGS_MY, // Настройки уведомлений о своих событиях
  'notifications.email.recipients': SETTINGS_USER_SCHEMA['notifications.email.recipients'],
  'notifications.sms.recipients': SETTINGS_USER_SCHEMA['notifications.sms.recipients'],

  'integrations.pyrus.credentials': SETTINGS_USER_SCHEMA['integrations.pyrus.credentials'],
  'billings.currency': SETTINGS_USER_SCHEMA['billings.currency']
}
exports.SETTINGS_USER_SCHEMA_RESELLER_CHILD = SETTINGS_USER_SCHEMA_RESELLER_CHILD

// Настройки, которые реселлер может менять для себя
const SETTINGS_USER_SCHEMA_RESELLER = {
  ...SETTINGS_USER_SCHEMA_ME, // Все что может менять себе обычный юзер
  ...NOTIFICATIONS_SETTINGS_CHILD // Настройки уведомлений о событиях дочернего юзера
}
exports.SETTINGS_USER_SCHEMA_RESELLER = SETTINGS_USER_SCHEMA_RESELLER

const SETTINGS_USER_SCHEMA_MANAGER_CHILD_READ = {
  'sendings.allowDuplicates': SETTINGS_USER_SCHEMA['sendings.allowDuplicates'],
  'sendings.duplicates.hashFields': SETTINGS_USER_SCHEMA['sendings.duplicates.hashFields'],
  'sendings.duplicates.timeout': SETTINGS_USER_SCHEMA['sendings.duplicates.timeout'],
  'sendings.rateLimit.maxAmount': SETTINGS_USER_SCHEMA['sendings.rateLimit.maxAmount'],
  'sendings.rateLimit.timeout': SETTINGS_USER_SCHEMA['sendings.rateLimit.timeout'],
  'sendings.sendernamesCheck': SETTINGS_USER_SCHEMA['sendings.sendernamesCheck'],
  'sendings.patternsCheck': SETTINGS_USER_SCHEMA['sendings.patternsCheck'],
  'sendings.tilda.sendFromForms': SETTINGS_USER_SCHEMA['sendings.tilda.sendFromForms'],
  'sendings.phoneNumber.checkMobile': SETTINGS_USER_SCHEMA['sendings.phoneNumber.checkMobile'],

  'billings.overdraft': SETTINGS_USER_SCHEMA['billings.overdraft'],
  'billings.autoAccrual': SETTINGS_USER_SCHEMA['billings.autoAccrual'],
  'billings.processChildrenOperations': SETTINGS_USER_SCHEMA['billings.processChildrenOperations'],
  'billings.billOnErrors': SETTINGS_USER_SCHEMA['billings.billOnErrors'],
  'billings.currency': SETTINGS_USER_SCHEMA['billings.currency'],
  'referral.billings.processedChildOperations': SETTINGS_USER_SCHEMA['referral.billings.processedChildOperations'],

  'websocket.channels': SETTINGS_USER_SCHEMA['websocket.channels'],

  'smpp.enabled': SETTINGS_USER_SCHEMA['smpp.enabled'],
  'smpp.rudeMode': SETTINGS_USER_SCHEMA['smpp.rudeMode'],
  'smpp.defaultMessageType': SETTINGS_USER_SCHEMA['smpp.defaultMessageType'],
  'smpp.ipWhitelist': SETTINGS_USER_SCHEMA['smpp.ipWhitelist'],

  webhooks: SETTINGS_USER_SCHEMA.webhooks,

  'notifications.enabled': SETTINGS_SITE_SCHEMA['notifications.enabled'],
  'notifications.events.billings.limit': SETTINGS_SITE_SCHEMA['notifications.events.billings.limit'],
  ...NOTIFICATIONS_SETTINGS_MY,
  ...NOTIFICATIONS_SETTINGS_CHILD,
  'notifications.email.recipients': SETTINGS_USER_SCHEMA['notifications.email.recipients'],
  'notifications.sms.recipients': SETTINGS_USER_SCHEMA['notifications.sms.recipients'],

  'integrations.pyrus.credentials': SETTINGS_USER_SCHEMA['integrations.pyrus.credentials']
}
exports.SETTINGS_USER_SCHEMA_MANAGER_CHILD_READ = SETTINGS_USER_SCHEMA_MANAGER_CHILD_READ

const SETTINGS_USER_SCHEMA_MANAGER_CHILD_WRITE = {
  'sendings.allowDuplicates': SETTINGS_USER_SCHEMA['sendings.allowDuplicates'],
  'sendings.duplicates.hashFields': SETTINGS_USER_SCHEMA['sendings.duplicates.hashFields'],
  'sendings.duplicates.timeout': SETTINGS_USER_SCHEMA['sendings.duplicates.timeout'],
  'sendings.rateLimit.maxAmount': SETTINGS_USER_SCHEMA['sendings.rateLimit.maxAmount'],
  'sendings.rateLimit.timeout': SETTINGS_USER_SCHEMA['sendings.rateLimit.timeout'],
  'sendings.sendernamesCheck': SETTINGS_USER_SCHEMA['sendings.sendernamesCheck'],
  'sendings.patternsCheck': SETTINGS_USER_SCHEMA['sendings.patternsCheck'],
  'sendings.tilda.sendFromForms': SETTINGS_USER_SCHEMA['sendings.tilda.sendFromForms'],
  'sendings.phoneNumber.checkMobile': SETTINGS_USER_SCHEMA['sendings.phoneNumber.checkMobile'],

  'notifications.enabled': SETTINGS_SITE_SCHEMA['notifications.enabled'],
  'notifications.events.billings.limit': SETTINGS_SITE_SCHEMA['notifications.events.billings.limit'],
  ...NOTIFICATIONS_SETTINGS_MY,
  ...NOTIFICATIONS_SETTINGS_CHILD,
  'notifications.email.recipients': SETTINGS_USER_SCHEMA['notifications.email.recipients'],
  'notifications.sms.recipients': SETTINGS_USER_SCHEMA['notifications.sms.recipients'],

  'integrations.pyrus.credentials': SETTINGS_USER_SCHEMA['integrations.pyrus.credentials']
}
exports.SETTINGS_USER_SCHEMA_MANAGER_CHILD_WRITE = SETTINGS_USER_SCHEMA_MANAGER_CHILD_WRITE

const SETTINGS_USER_FIELDS_MANAGER_CHILD_RO = Object.keys(SETTINGS_USER_SCHEMA_MANAGER_CHILD_READ).filter(key => !Object.keys(SETTINGS_USER_SCHEMA_MANAGER_CHILD_WRITE).includes(key))
exports.SETTINGS_USER_FIELDS_MANAGER_CHILD_RO = SETTINGS_USER_FIELDS_MANAGER_CHILD_RO

// Это не схема а набор значений для подмешивания в настройки администраторов
exports.SETTINGS_USER_SCHEMA_SUDO = {
  'sendings.sendernamesCheck': [],
  'sendings.patternsCheck': [],
  'sendings.allowDuplicates': true,

  'websocket.channels': [ ...WEBSOCKET_CHANNELS, 'notifications', 'progress' ]
}
